import { z } from 'zod';
import { ROUTE_STATUSES, ROUTE_STOP_STATUSES, STOP_SKIP_REASONS } from '../constants/index.js';
import { dateOnlySchema, latLngSchema, uuidSchema } from './common.js';
export const createRouteSchema = z.object({
    /** The business day, `YYYY-MM-DD` in Europe/Rome. */
    routeDate: dateOnlySchema,
    name: z.string().trim().max(120).nullish(),
    assignedUserId: uuidSchema.nullish(),
    startWarehouseId: uuidSchema.nullish(),
    endWarehouseId: uuidSchema.nullish(),
    notes: z.string().trim().max(2000).nullish(),
    /** Customers to seed the route with, in the given order. */
    customerIds: z.array(uuidSchema).max(200).optional(),
});
export const updateRouteSchema = z.object({
    routeDate: dateOnlySchema.optional(),
    name: z.string().trim().max(120).nullish(),
    assignedUserId: uuidSchema.nullish(),
    startWarehouseId: uuidSchema.nullish(),
    endWarehouseId: uuidSchema.nullish(),
    notes: z.string().trim().max(2000).nullish(),
    /**
     * Status is changed through this field, but only along the allowed path:
     * draft → assigned → started → completed. The server enforces the order and
     * the preconditions; it is not a free-form write.
     */
    status: z.enum(ROUTE_STATUSES).optional(),
});
export const addRouteStopsSchema = z.object({
    /** Appended after the existing stops, in the order given. */
    customerIds: z.array(uuidSchema).min(1).max(200),
});
export const reorderRouteStopsSchema = z.object({
    /** Every stop of the route, in the new order. Partial lists are rejected. */
    stopIds: z.array(uuidSchema).min(1).max(200),
});
export const updateRouteStopSchema = z
    .object({
    status: z.enum(ROUTE_STOP_STATUSES).optional(),
    skipReason: z.enum(STOP_SKIP_REASONS).nullish(),
    skipNote: z.string().trim().max(500).nullish(),
    notes: z.string().trim().max(1000).nullish(),
})
    .refine((v) => v.status !== 'skipped' || Boolean(v.skipReason), {
    message: 'routes.errors.skipReasonRequired',
    path: ['skipReason'],
})
    .refine((v) => v.skipReason !== 'other' || Boolean(v.skipNote?.trim()), {
    message: 'routes.errors.skipNoteRequired',
    path: ['skipNote'],
});
/** Copies a route to another day, keeping its stops and their order. */
export const duplicateRouteSchema = z.object({
    routeDate: dateOnlySchema,
    assignedUserId: uuidSchema.nullish(),
    name: z.string().trim().max(120).nullish(),
});
export const routeStopSchema = z.object({
    id: uuidSchema,
    customerId: uuidSchema,
    customerCode: z.string(),
    customerName: z.string(),
    customerAddress: z.string().nullable(),
    customerComune: z.string().nullable(),
    location: latLngSchema.nullable(),
    geofenceRadiusMeters: z.number().int(),
    deliveryWindowStart: z.string().nullable(),
    deliveryWindowEnd: z.string().nullable(),
    sequence: z.number().int(),
    status: z.enum(ROUTE_STOP_STATUSES),
    skipReason: z.enum(STOP_SKIP_REASONS).nullable(),
    skipNote: z.string().nullable(),
    arrivedAt: z.string().nullable(),
    departedAt: z.string().nullable(),
    dwellSeconds: z.number().int().nullable(),
    distanceFromPreviousMeters: z.number().int().nullable(),
    durationFromPreviousSeconds: z.number().int().nullable(),
    notes: z.string().nullable(),
});
export const routeSchema = z.object({
    id: uuidSchema,
    routeDate: z.string(),
    name: z.string().nullable(),
    assignedUserId: uuidSchema.nullable(),
    assignedUserName: z.string().nullable(),
    vehicleId: uuidSchema.nullable(),
    vehiclePlate: z.string().nullable(),
    status: z.enum(ROUTE_STATUSES),
    startWarehouseId: uuidSchema.nullable(),
    endWarehouseId: uuidSchema.nullable(),
    plannedDistanceMeters: z.number().int().nullable(),
    plannedDurationSeconds: z.number().int().nullable(),
    /** True when stops changed after the geometry was last computed. */
    geometryStale: z.boolean(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    notes: z.string().nullable(),
    stopCount: z.number().int(),
    visitedCount: z.number().int(),
    skippedCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** A route with its stops and planned line, for the editor and the mobile app. */
export const routeDetailSchema = routeSchema.extend({
    stops: z.array(routeStopSchema),
    plannedGeometry: z.array(latLngSchema).nullable(),
});
export const routeListQueryExtras = z.object({
    /** Inclusive date range, both optional. */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    assignedUserId: uuidSchema.optional(),
    status: z.enum(ROUTE_STATUSES).optional(),
});
/** What the optimiser proposes, before it is applied. */
export const optimizationResultSchema = z.object({
    stopIds: z.array(uuidSchema),
    distanceMeters: z.number().int(),
    durationSeconds: z.number().int(),
    /** Saving against the order the route had before optimising. */
    previousDistanceMeters: z.number().int().nullable(),
});
//# sourceMappingURL=route.js.map